import { GlobalStateController } from "bai-react-global-state";

import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { _ } from "js/vendor";
import { app } from "js/namespaces";
import { IUserStorageModel } from "common/interfaces";
import { QuickStartTask } from "common/constants";

const logger = getLogger(LogGroup.CONTROLLERS);

interface QuickStartControllerState {
    isInitialized: boolean;
    quickStartTasks: Record<string, boolean>;
    isOpen: boolean;
    forceOpen: boolean;
}

const initialState: QuickStartControllerState = {
    isInitialized: false,
    quickStartTasks: {},
    isOpen: false,
    forceOpen: false
};

class QuickStartController extends GlobalStateController<QuickStartControllerState> {
    private _user: IUserStorageModel | null;

    constructor(initialState: QuickStartControllerState) {
        super(initialState);
        this._user = null;
    }

    areAllTasksDone = () => {
        return Object.values(this.quickStartTasks || {}).every(task => task);
    }

    get quickStartTasks() {
        return this._state.quickStartTasks;
    }

    get taskKeys() {
        return Object.keys(this.quickStartTasks);
    }

    get forceOpen() {
        return this._state.forceOpen;
    }

    setForceOpen(forceOpen: boolean) {
        return this._updateState({ forceOpen });
    }

    get isInitialized() {
        return this._state.isInitialized;
    }

    async initialize() {
        // AppController is guaranteed to be initialized here, no need to check user here
        this._user = AppController.user;

        logger.info("[QuickStartController] initialize()", { userId: this._user.id });

        const quickStartTasks = this._user.get("quickStartTasks") || {};

        const allTasksDone = Object.values(quickStartTasks).every(task => task);

        this._updateState({
            quickStartTasks,
            // if all tasks are done or there are no tasks, we consider the controller not initialized
            isInitialized: !_.isEmpty(quickStartTasks) && !allTasksDone
        });
    }

    async updateTask(taskKey: string, done: boolean) {
        const { quickStartTasks } = this._state;

        // don't update if there are no quick start tasks or the controller is not initialized
        if (_.isEmpty(quickStartTasks) || !this.isInitialized) {
            return;
        }

        const updatedQuickStartTasks = {
            ...quickStartTasks,
            [taskKey]: done
        };

        try {
            this._user.update({ quickStartTasks: updatedQuickStartTasks });
            await this._user.updatePromise;
            await this._updateState({ quickStartTasks: updatedQuickStartTasks });
        } catch (err) {
            logger.error(err, "[QuickStartController] updateTask() error updating user");
            // Revert local state on error
            await this._updateState({ quickStartTasks });
        }
    }

    toggleTask(taskLabel: string) {
        const currentDone = this.quickStartTasks[taskLabel] || false;
        return this.updateTask(taskLabel, !currentDone);
    }

    getTaskStatus(taskLabel: string): boolean {
        return this.quickStartTasks[taskLabel] || false;
    }

    async markFirstSmartSlideTaskComplete() {
        // don't update if the controller is not initialized or the task is already completed
        if (!this.isInitialized || this.getTaskStatus(QuickStartTask.ADD_YOUR_FIRST_SMART_SLIDE)) {
            return;
        }

        // only update if the task is not already completed
        await this.updateTask(QuickStartTask.ADD_YOUR_FIRST_SMART_SLIDE, true);
    }

    async resetAllTasks() {
        const currentQuickStartTasks = this._user.get("quickStartTasks") || {};
        const taskKeys = Object.keys(currentQuickStartTasks);

        // Create a new object with all tasks set to false
        const resetQuickStartTasks: Record<string, boolean> = {};
        taskKeys.forEach(key => {
            resetQuickStartTasks[key] = false;
        });

        // Update user model
        try {
            this._user.update({
                quickStartTasks: resetQuickStartTasks
            });
            await this._user.updatePromise;
            await this._updateState({ quickStartTasks: resetQuickStartTasks });
        } catch (err) {
            logger.error(err, "[QuickStartController] resetAllTasks() error updating user");
            // Revert state on error
            await this._updateState({ quickStartTasks: currentQuickStartTasks });
        }
    }

    async reset() {
        logger.info("[QuickStartController] reset()");
        await this._updateState(() => _.cloneDeep(initialState));
    }
}

const quickStartController = new QuickStartController(_.cloneDeep(initialState));

// for lazy import and debug
app.quickStartController = quickStartController;

export default quickStartController;

